// Shared "Recent activity" panel data (public mode only).
//
// Extracted out of the mobile-web `useHomeDashboard` so the extension's full-tab
// dashboard side panel can render the same rows. Wires the REAL
// ActivityController under a session, exactly like the full Activity screen —
// without it the panel never receives ops and is stuck on its empty state.
//
// Session ownership: pass `sessionId` when the caller already registered one
// (and resets it on unmount) — mobile-web's home shares its session with the
// asset-type tabs. Omit it and this hook creates + tears down its own.
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { ActivityItem } from '@mobile/modules/dashboard/screens/home/components/ActivityCard'
import { accountOpToActivityItem } from '@mobile/modules/dashboard/screens/home/recentActivity'
import useActivityControllerState from '@web/hooks/useActivityControllerState'
import useBackgroundService from '@web/hooks/useBackgroundService'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'

// How many ops to fetch and how many to display.
export const RECENT_ACTIVITY_FETCH = 10
export const RECENT_ACTIVITY_SHOW = 4
export const RECENT_ACTIVITY_WINDOW_MS = 24 * 60 * 60 * 1000

type Params = {
  isPrivate: boolean
  /** Reuse an existing activity session instead of registering a private one. */
  sessionId?: string
  /**
   * Age cut-off for the returned ops. Omit (the default) to return the latest
   * ops whatever their age — a wallet whose last transaction is older than the
   * window would otherwise show an empty panel while the Activity tab is full,
   * which reads as a bug. Callers that pass one must label the card to match
   * (ActivityCard.subtitle).
   */
  windowMs?: number
}

const useDashboardRecentActivity = ({
  isPrivate,
  sessionId: externalSessionId,
  windowMs
}: Params) => {
  const { dispatch } = useBackgroundService()
  const { account } = useSelectedAccountControllerState()
  const { accountsOps } = useActivityControllerState()
  const [ownSessionId] = useState(() => `dashboard-recent-${nanoid()}`)
  const sessionId = externalSessionId || ownSessionId
  const ownsSession = !externalSessionId

  // Terminate the session on unmount — only when this hook created it; a caller
  // that passed its own sessionId resets it itself (it may still be in use by
  // other surfaces sharing that session).
  useEffect(() => {
    if (!ownsSession) return
    return () => {
      dispatch({ type: 'MAIN_CONTROLLER_ACTIVITY_RESET_ACC_OPS_FILTERS', params: { sessionId } })
    }
  }, [ownsSession, dispatch, sessionId])

  const dispatchRecentFilters = useCallback(() => {
    if (!account?.addr) return
    dispatch({
      type: 'MAIN_CONTROLLER_ACTIVITY_SET_ACC_OPS_FILTERS',
      params: {
        sessionId,
        filters: { account: account.addr },
        pagination: { itemsPerPage: RECENT_ACTIVITY_FETCH, fromPage: 0 }
      }
    })
  }, [account?.addr, dispatch, sessionId])

  useEffect(() => {
    if (isPrivate) return
    dispatchRecentFilters()
  }, [isPrivate, dispatchRecentFilters])

  // Self-heal a session left on the PREVIOUS account: the ActivityController's
  // background `syncFilteredAccountsOps` re-applies each session's stored filters
  // and can race/clobber our account-switch dispatch back to the old account
  // (last async write wins; mobile-web's storage round-trip widens the window).
  // Re-fire (debounced) until the stored session matches the current account, or
  // the recent-activity panel stays stuck on the previous account's ops. Mirrors
  // the same guard in useActivityScreen.
  const recentSession = accountsOps?.[sessionId]
  const recentSessionStale =
    !isPrivate &&
    !!account?.addr &&
    !!recentSession &&
    recentSession.filters.account !== account.addr
  useEffect(() => {
    if (!recentSessionStale) return
    const id = setTimeout(dispatchRecentFilters, 400)
    return () => clearTimeout(id)
  }, [recentSessionStale, dispatchRecentFilters])

  const recentActivity = useMemo<ActivityItem[]>(() => {
    if (isPrivate) return []
    const session = accountsOps?.[sessionId]
    // Stable sessionId: after an account switch this entry still holds the
    // previous account's ops until the re-dispatched filter round-trips. Only
    // trust the result once its filters match the current account, or the panel
    // flashes the old account's history.
    if (!session || session.filters.account !== account?.addr) return []
    const cutoff = windowMs ? Date.now() - windowMs : 0
    return (
      session.result.items
        // Keep still-pending ops (timestamp 0) regardless of the window.
        .filter((op) => !op.timestamp || op.timestamp >= cutoff)
        .slice(0, RECENT_ACTIVITY_SHOW)
        .map(accountOpToActivityItem)
        .filter((i): i is NonNullable<typeof i> => !!i)
    )
  }, [isPrivate, accountsOps, sessionId, account?.addr, windowMs])

  return { recentActivity, sessionId }
}

export default useDashboardRecentActivity
